import { useEffect } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import PageHero from '../../components/base/PageHero';

export default function AboutPage() {
  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
    
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "AboutPage",
      "name": "私たちについて - ジュエリーおかやま株式会社",
      "description": "岡山市のジュエリーおかやま株式会社の会社概要、事業内容、スタッフ紹介。想いを紡ぐジュエリーづくりを大切にしています。",
      "url": `${siteUrl}/about`,
      "mainEntity": {
        "@type": "Organization",
        "name": "ジュエリーおかやま株式会社",
        "foundingDate": "2000",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "北区○○町1-2-3",
          "addressLocality": "岡山市",
          "addressRegion": "岡山県",
          "addressCountry": "JP"
        }
      }
    });
    document.head.appendChild(script);
    
    return () => {
      document.head.removeChild(script);
    };
  }, []);

  return (
    <div className="min-h-screen">
      <Header />
      <main>
        <PageHero
          title="私たちについて"
          subtitle="想いを紡ぐ、ジュエリーづくり"
          image="https://static.readdy.ai/image/2333ec8ce0f23b8d6e87acea74053a0e/8db486f03b04d56712284208f33f85ba.jpeg"
        />
        
        {/* Concept Section */}
        <section className="py-20 lg:py-28 bg-cream-100">
          <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 items-center">
              <div className="lg:col-span-3">
                <h2 className="text-3xl md:text-4xl lg:text-5xl font-serif font-semibold text-brown-600 mb-8">
                  想いを紡ぐ、ジュエリーづくり
                </h2>
                <div className="space-y-6 text-base md:text-lg text-brown-500 leading-relaxed font-serif">
                  <p>
                    ジュエリーおかやま株式会社は、2000年の創業以来、岡山市を中心に地域の皆様に愛されてきました。私たちが大切にしているのは、お客様一人ひとりの<strong className="text-brown-600">想いをカタチにする</strong>こと。
                  </p>
                  <p>
                    結婚という人生の節目を迎えるカップル、大切な記念日を祝うご夫婦、祖父母や親から受け継いだジュエリーを新しい形で残したい方。それぞれの物語に寄り添い、世界に一つだけのジュエリーをお作りしています。
                  </p>
                  <p>
                    熟練の職人が一つひとつ手作業で仕上げる<strong className="text-brown-600">オーダーメイドジュエリー</strong>、想い出を受け継ぐ<strong className="text-brown-600">リメイク・修理</strong>、そして人生の門出を祝う<strong className="text-brown-600">ブライダルジュエリー</strong>。岡山の地で、お客様の大切な瞬間に寄り添い続けます。
                  </p>
                  <p>
                    私たちのアトリエは、岡山市北区にございます。ぜひ一度お越しいただき、職人の技と想いをご覧ください。
                  </p>
                </div>
              </div>
              <div className="lg:col-span-2">
                <div className="w-full h-[500px] rounded-xl overflow-hidden shadow-xl">
                  <img
                    src="https://public.readdy.ai/ai/img_res/adeb16e9f8519cae977fee3c18c1cf06.jpg"
                    alt="ジュエリーおかやまのアトリエ"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Services Section */}
        <section className="py-20 lg:py-28 bg-white">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-serif font-semibold text-center text-brown-600 mb-16">
              事業内容
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
              <article className="bg-cream-50 rounded-xl p-8 hover:shadow-xl transition-shadow">
                <div className="w-16 h-16 flex items-center justify-center bg-brown-400 rounded-full mb-6 mx-auto">
                  <i className="ri-pencil-ruler-2-line text-3xl text-white"></i>
                </div>
                <h3 className="text-2xl font-serif font-semibold text-brown-600 text-center mb-4">
                  オーダーメイド
                </h3>
                <p className="text-base text-brown-500 leading-relaxed font-serif mb-6">
                  お客様のご要望をじっくりとお伺いし、デザインから素材選び、制作まで、すべてを一貫してサポートいたします。世界に一つだけの特別なジュエリーをお作りします。
                </p>
                <div className="w-full h-48 rounded-lg overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=custom%20jewelry%20design%20sketches%20and%20gemstones%20on%20workbench%20professional%20jewelry%20making%20tools%20cream%20background%20detailed%20craftsmanship%20high%20quality%20product%20photography&width=600&height=400&seq=service1&orientation=landscape"
                    alt="オーダーメイドジュエリー"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </article>

              <article className="bg-cream-50 rounded-xl p-8 hover:shadow-xl transition-shadow">
                <div className="w-16 h-16 flex items-center justify-center bg-brown-400 rounded-full mb-6 mx-auto">
                  <i className="ri-refresh-line text-3xl text-white"></i>
                </div>
                <h3 className="text-2xl font-serif font-semibold text-brown-600 text-center mb-4">
                  リメイク・修理
                </h3>
                <p className="text-base text-brown-500 leading-relaxed font-serif mb-6">
                  祖父母や親から受け継いだ大切なジュエリーを、現代的なデザインに生まれ変わらせます。想い出を大切に残しながら、新しい形でお楽しみいただけます。
                </p>
                <div className="w-full h-48 rounded-lg overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=vintage%20antique%20jewelry%20being%20restored%20by%20craftsman%20hands%20before%20and%20after%20transformation%20warm%20lighting%20cream%20background%20professional%20jewelry%20repair%20high%20quality%20photography&width=600&height=400&seq=service2&orientation=landscape"
                    alt="ジュエリーリメイク・修理"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </article>

              <article className="bg-cream-50 rounded-xl p-8 hover:shadow-xl transition-shadow">
                <div className="w-16 h-16 flex items-center justify-center bg-brown-400 rounded-full mb-6 mx-auto">
                  <i className="ri-heart-line text-3xl text-white"></i>
                </div>
                <h3 className="text-2xl font-serif font-semibold text-brown-600 text-center mb-4">
                  ブライダル
                </h3>
                <p className="text-base text-brown-500 leading-relaxed font-serif mb-6">
                  結婚という人生の大切な節目に、お二人の愛を象徴する特別な指輪をご提案します。婚約指輪から結婚指輪まで、岡山で最高のブライダルジュエリーを。
                </p>
                <div className="w-full h-48 rounded-lg overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=beautiful%20wedding%20rings%20and%20engagement%20rings%20displayed%20elegantly%20on%20soft%20cream%20velvet%20romantic%20lighting%20luxury%20bridal%20jewelry%20high%20quality%20product%20photography&width=600&height=400&seq=service3&orientation=landscape"
                    alt="ブライダルジュエリー"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </article>
            </div>
          </div>
        </section>

        {/* Staff Section */}
        <section className="py-20 lg:py-28 bg-cream-100">
          <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-serif font-semibold text-center text-brown-600 mb-16">
              スタッフ紹介
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
              <article className="bg-white rounded-xl p-8 text-center shadow-lg hover:shadow-xl transition-shadow">
                <div className="w-32 h-32 mx-auto mb-6 rounded-full overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=professional%20japanese%20businessman%20in%20his%2050s%20wearing%20elegant%20business%20attire%20warm%20smile%20confident%20expression%20cream%20background%20high%20quality%20portrait%20photography%20company%20president%20sophisticated%20appearance&width=300&height=300&seq=staff1&orientation=squarish"
                    alt="代表取締役 岡山太郎"
                    className="w-full h-full object-cover"
                  />
                </div>
                <h3 className="text-xl font-serif font-semibold text-brown-600 mb-2">
                  岡山 太郎
                </h3>
                <p className="text-sm text-brown-400 font-serif mb-4">代表取締役</p>
                <p className="text-base text-brown-500 leading-relaxed font-serif">
                  創業以来、岡山の皆様に支えられてきました。お客様の想いを大切に、これからも真心込めたジュエリーづくりを続けてまいります。
                </p>
              </article>

              <article className="bg-white rounded-xl p-8 text-center shadow-lg hover:shadow-xl transition-shadow">
                <div className="w-32 h-32 mx-auto mb-6 rounded-full overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=skilled%20japanese%20jewelry%20craftsman%20in%20his%2040s%20wearing%20work%20apron%20gentle%20smile%20professional%20appearance%20cream%20background%20high%20quality%20portrait%20photography%20master%20artisan%20experienced%20craftsman&width=300&height=300&seq=staff2&orientation=squarish"
                    alt="チーフデザイナー"
                    className="w-full h-full object-cover"
                  />
                </div>
                <h3 className="text-xl font-serif font-semibold text-brown-600 mb-2">
                  田中 健一
                </h3>
                <p className="text-sm text-brown-400 font-serif mb-4">チーフデザイナー</p>
                <p className="text-base text-brown-500 leading-relaxed font-serif">
                  20年以上の経験を活かし、お客様のご要望を形にします。細部までこだわった美しいジュエリーをお作りいたします。
                </p>
              </article>

              <article className="bg-white rounded-xl p-8 text-center shadow-lg hover:shadow-xl transition-shadow">
                <div className="w-32 h-32 mx-auto mb-6 rounded-full overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=friendly%20japanese%20woman%20staff%20in%20her%2030s%20wearing%20elegant%20business%20attire%20warm%20welcoming%20smile%20cream%20background%20high%20quality%20portrait%20photography%20customer%20service%20professional%20appearance&width=300&height=300&seq=staff3&orientation=squarish"
                    alt="カスタマーサポート"
                    className="w-full h-full object-cover"
                  />
                </div>
                <h3 className="text-xl font-serif font-semibold text-brown-600 mb-2">
                  佐藤 美咲
                </h3>
                <p className="text-sm text-brown-400 font-serif mb-4">カスタマーサポート</p>
                <p className="text-base text-brown-500 leading-relaxed font-serif">
                  お客様のご相談に丁寧にお応えします。初めての方も安心してご来店ください。皆様のお越しをお待ちしております。
                </p>
              </article>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}